import type { TrainingAccessResponse, TrainingProgress } from '../types'

const STORAGE_KEY = 'agentTrainingProgress'

const defaultModules: TrainingProgress[] = [
  {
    moduleId: 'enrollment-101',
    title: 'Client Enrollment Basics',
    description: 'Learn how to guide clients through the enrollment workflow.',
    moduleType: 'ENROLLMENT',
    isRequired: true,
    isCompleted: false,
    timeSpent: 0,
    videoUrl: '',
  },
  {
    moduleId: 'self-enrollment',
    title: 'Self-Enrollment Overview',
    description: 'Understand how clients complete self-enrollment flows.',
    moduleType: 'SELF_ENROLLMENT',
    isRequired: false,
    isCompleted: false,
    timeSpent: 0,
    videoUrl: '',
  },
  {
    moduleId: 'compliance-essentials',
    title: 'Compliance Essentials',
    description: 'Review core compliance policies and procedures.',
    moduleType: 'COMPLIANCE',
    isRequired: true,
    isCompleted: false,
    timeSpent: 0,
    videoUrl: '',
  },
]

function loadProgress(): TrainingProgress[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as TrainingProgress[]
      return parsed.map((module) => ({
        ...module,
        timeSpent: module.timeSpent ?? 0,
      }))
    }
  } catch (error) {
    console.warn('Failed to load training progress from storage:', error)
  }

  return defaultModules
}

function saveProgress(modules: TrainingProgress[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(modules))
  } catch (error) {
    console.warn('Failed to persist training progress:', error)
  }
}

export const TrainingApi = {
  async checkUserAccess(moduleType: string): Promise<TrainingAccessResponse> {
    const response: TrainingAccessResponse = {
      hasAccess: true,
      lastCheckedAt: new Date().toISOString(),
    }

    // Restrict access to required modules for now (can be extended later)
    if (moduleType === 'ENROLLMENT' || moduleType === 'SELF_ENROLLMENT') {
      return response
    }

    return response
  },

  async getUserProgress(): Promise<TrainingProgress[]> {
    return loadProgress()
  },

  async markModuleCompleted(moduleId: string): Promise<void> {
    const modules = loadProgress()
    const next = modules.map((module) =>
      module.moduleId === moduleId
        ? {
            ...module,
            isCompleted: true,
            timeSpent: module.timeSpent ?? 0,
          }
        : module
    )

    saveProgress(next)
  },

  async getModuleById(moduleId: string): Promise<TrainingProgress | null> {
    const modules = loadProgress()
    return modules.find((module) => module.moduleId === moduleId) ?? null
  },
}

